import { useCallback, useEffect, useRef, useState } from 'react';

import DesktopNav from './DesktopNav';
import MobileNav from './MobileNav';
import SystemAlert from './SystemAlert';
import {
  HeaderLogoFragmentFragment,
  HeaderMenuItemFragmentFragment,
  MenuButtonFragmentFragment,
  SystemAlertFragmentFragment,
} from '../../__generated__/graphql';

import style from './header.module.css';

interface HeaderProps {
  headerLogo: HeaderLogoFragmentFragment | undefined;
  menuButton: MenuButtonFragmentFragment;
  menuItems: HeaderMenuItemFragmentFragment[];
  systemAlert: SystemAlertFragmentFragment;
}

const Header = ({
  headerLogo,
  menuButton,
  menuItems,
  systemAlert,
}: HeaderProps) => {
  const headerRef = useRef<HTMLElement | null>(null);

  const [clientHeight, setClientHeight] = useState(0);

  const handleResize = useCallback(() => {
    if (headerRef.current) {
      setClientHeight(headerRef.current.clientHeight);
    }
  }, []);

  useEffect(() => {
    if (headerRef.current) {
      setClientHeight(headerRef.current.clientHeight);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    // update the CSS variable --header-height to the current header height
    if (headerRef.current) {
      const root = document.documentElement;
      root.style.setProperty('--header-height', `${clientHeight.toString()}px`);
    }
  }, [clientHeight]);

  return (
    <header
      className={style.header}
      ref={headerRef}
    >
      {systemAlert.systemAlert?.message && (
        <SystemAlert
          alertId={systemAlert.systemAlert.alertId ?? ''}
          end={systemAlert.systemAlert.end ?? null}
          message={systemAlert.systemAlert.message}
          onHideOrShow={handleResize}
          start={systemAlert.systemAlert.start ?? null}
        />
      )}

      <DesktopNav
        className={style.desktopNav}
        headerLogo={headerLogo}
        menuButton={menuButton}
        menuItems={menuItems}
      />

      <MobileNav
        className={style.mobileNav}
        headerLogo={headerLogo}
        menuButton={menuButton}
        menuItems={menuItems}
      />
    </header>
  );
};

export default Header;
